import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, BookOpen, CreditCard, Settings, Bell, Shield,
  Download, HelpCircle, ChevronRight, ChevronDown, ArrowRight,
} from 'lucide-react';
import { clsx } from 'clsx';

const categories = [
  {
    icon: BookOpen,
    title: 'Primeiros Passos',
    description: 'Como criar conta, configurar e começar a usar o MyBank.',
    color: 'bg-primary-50 dark:bg-primary-900/20 text-primary-600',
    articles: [
      'Como criar minha conta no MyBank?',
      'Como cadastrar minha primeira conta bancária?',
      'Como registrar minha primeira transação?',
      'O que é o plano gratuito e quais são os limites?',
      'Como fazer o onboarding completo?',
    ],
  },
  {
    icon: CreditCard,
    title: 'Transações e Contas',
    description: 'Cadastro de transações, importação e gestão de contas.',
    color: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600',
    articles: [
      'Como categorizar transações automaticamente?',
      'Como importar extrato bancário?',
      'Como registrar parcelamentos?',
      'Como criar recorrências e despesas fixas?',
      'Como fazer transferência entre contas?',
    ],
  },
  {
    icon: Settings,
    title: 'Configurações',
    description: 'Perfil, segurança, preferências e personalização.',
    color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600',
    articles: [
      'Como alterar minha senha?',
      'Como ativar autenticação em dois fatores?',
      'Como personalizar categorias?',
      'Como mudar o tema para escuro?',
      'Como excluir minha conta?',
    ],
  },
  {
    icon: Bell,
    title: 'Notificações',
    description: 'Alertas, lembretes e digests por e-mail.',
    color: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600',
    articles: [
      'Como receber alertas de saldo baixo?',
      'Como configurar o resumo semanal por e-mail?',
      'Como desativar notificações específicas?',
      'Como receber lembretes de vencimento?',
    ],
  },
  {
    icon: Shield,
    title: 'Planos e Assinatura',
    description: 'Upgrade, cancelamento e faturamento.',
    color: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600',
    articles: [
      'Qual a diferença entre os planos?',
      'Como fazer upgrade para o Pro?',
      'Como cancelar minha assinatura?',
      'Minha cobrança falhou, o que fazer?',
      'Posso pedir reembolso?',
    ],
  },
  {
    icon: Download,
    title: 'Exportação e Dados',
    description: 'Exportar dados, backup e portabilidade.',
    color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600',
    articles: [
      'Como exportar transações em CSV?',
      'Como fazer backup completo dos dados?',
      'Os meus dados são privados?',
      'Como importar dados de outro app?',
    ],
  },
];

const faqs = [
  {
    q: 'O MyBank acessa minha conta bancária?',
    a: 'Não. O MyBank não se conecta diretamente a bancos. Você registra suas transações manualmente ou importa via arquivo de extrato (OFX/CSV). Seus dados bancários nunca são compartilhados conosco.',
  },
  {
    q: 'Meus dados financeiros são seguros?',
    a: 'Sim. Todos os dados são criptografados em trânsito (TLS via AWS) e em repouso (AES-256 via DynamoDB). Nunca vendemos ou compartilhamos seus dados com terceiros. Cumprimos integralmente a LGPD.',
  },
  {
    q: 'Posso usar o MyBank em vários dispositivos?',
    a: 'Sim. O MyBank é acessível via navegador em qualquer dispositivo. Seus dados ficam sincronizados em tempo real.',
  },
  {
    q: 'O plano gratuito tem limite de tempo?',
    a: 'Não. O plano Free é gratuito para sempre, não é um trial. Você pode usar indefinidamente com os limites do plano (2 contas, 50 transações/mês).',
  },
  {
    q: 'Como funciona o plano Família?',
    a: 'Um titular cria a conta e convida até 4 pessoas. Cada membro tem seu próprio login e o titular define as permissões de cada um (só visualização, ou edição completa).',
  },
  {
    q: 'Posso cancelar quando quiser?',
    a: 'Sim. Não há contrato ou fidelidade. Você cancela quando quiser direto nas configurações da conta, sem precisar falar com ninguém.',
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 dark:border-gray-800 last:border-0">
      <button
        className="w-full flex items-center justify-between py-5 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium text-gray-900 dark:text-white pr-4">{q}</span>
        <ChevronDown size={18} className={clsx('text-gray-400 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <p className="pb-5 text-gray-600 dark:text-gray-400 leading-relaxed text-sm">{a}</p>
      )}
    </div>
  );
};

const Help = () => {
  const [search, setSearch] = useState('');

  const filteredCategories = categories.map((cat) => ({
    ...cat,
    articles: cat.articles.filter((a) =>
      !search || a.toLowerCase().includes(search.toLowerCase()) || cat.title.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => !search || cat.articles.length > 0);

  return (
    <div className="bg-white dark:bg-gray-950 min-h-screen">
      {/* Header */}
      <section className="py-20 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <HelpCircle size={24} className="text-primary-400" />
            <span className="text-primary-400 font-semibold">Central de Ajuda</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">Como podemos ajudar?</h1>
          <p className="text-gray-400 mb-8">Busque por artigos, tutoriais e perguntas frequentes.</p>

          {/* Search */}
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar artigos... (ex: como exportar CSV)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-5 py-4 rounded-2xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Categories grid */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            {search ? `Resultados para "${search}"` : 'Categorias de ajuda'}
          </h2>

          {filteredCategories.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 mb-4">Nenhum artigo encontrado para "{search}"</p>
              <Link to="/contact" className="text-primary-600 font-medium hover:text-primary-700">
                Falar com o suporte →
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCategories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <div key={cat.title} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 hover:border-primary-400 dark:hover:border-primary-600 transition-all hover:shadow-md">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${cat.color}`}>
                      <Icon size={20} />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-1">{cat.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{cat.description}</p>
                    <ul className="space-y-2">
                      {cat.articles.slice(0, 4).map((article) => (
                        <li key={article}>
                          <button className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 transition-colors text-left group">
                            <ChevronRight size={14} className="text-gray-300 dark:text-gray-600 group-hover:text-primary-500 flex-shrink-0" />
                            {article}
                          </button>
                        </li>
                      ))}
                    </ul>
                    {cat.articles.length > 4 && (
                      <button className="mt-3 text-xs text-primary-600 font-medium hover:text-primary-700">
                        +{cat.articles.length - 4} artigos
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FAQ */}
        {!search && (
          <div className="max-w-3xl mx-auto mb-20">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Perguntas Frequentes</h2>
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 px-6">
              {faqs.map((faq) => <FAQItem key={faq.q} {...faq} />)}
            </div>
          </div>
        )}

        {/* Contact CTA */}
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl p-10 text-center text-white">
          <HelpCircle size={36} className="mx-auto mb-4 opacity-80" />
          <h3 className="text-2xl font-bold mb-3">Não encontrou o que procurava?</h3>
          <p className="text-white/80 mb-6">Nossa equipe está pronta para ajudar. Respondemos em até 24h úteis.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
          >
            Falar com o suporte
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Help;
